import React, {
    useCallback,
    useEffect,
    useMemo,
    useContext, useState, useRef
} from 'react';
import { useValidation } from 'gw-portals-validation-react';
import { ViewModelForm, ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { useSgHandleSubQuestions, SgSessionStorageService } from 'saga-modules';
import CommonConstants from 'sg-common-constants';
import SpmConstants from 'sg-spm-constants';
import SagaCommonMessages from 'sg-common-messages';
import SgSelect from 'sg-select';
import { SgDate, SgDateInputTypes } from 'sg-date';
import SpmUtils from 'sg-spm-utils';
import { TranslatorContext } from '@jutro/locale';
import _ from 'lodash';
import moment from 'moment';
import { events } from 'gw-platform-events-js';
import { WizardContext } from 'gw-portals-wizard-react';
import { Link } from '@jutro/components';
import metadata from './SgSpmClaimForm.metadata.json5';
import messages from '../SgSpmClaimDetails.messages';

function SgSpmClaimForm(props) {
    const {
        id,
        modelVM,
        path,
        updateData,
        onValidate: onValidateParent,
        onSaveClaim: onSaveClaimParent,
        onCancelClaim,
        showErrors,
        showClaimNumberHeader,
        isAggregator,
        claimHeaderMessage,
    } = props;

    const {
        isComponentValid,
        onValidate
    } = useValidation(id);

    const translator = useContext(TranslatorContext);
    const wizardContext = useContext(WizardContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const [invalidSubQuestions, updateInvalidSubQuestions] = useState(
        SgSessionStorageService.getItem(SgSessionStorageService.KEY.INVALID_SUB_QUESTIONS)
    );
    const hasSavedClaim = useRef(false);
    const [faultStatusSubLabelExpanded, setFaultStatusSubLabelExpanded] = useState(false);

    const selectedClaim = useMemo(() => {
        return _.get(modelVM, path);
    }, [modelVM, path]);

    const { driverIndex, claimIndex } = useMemo(() => {
        return {
            driverIndex: Number(path.match(/\d+/g)[0]),
            claimIndex: Number(path.match(/\d+/g)[1]),
        };
    }, [path]);

    const adobeSuffix = useMemo(() => {
        return `driver${driverIndex}claim${claimIndex}`;
    }, [claimIndex, driverIndex]);

    const possessivePronoun = useMemo(() => {
        return driverIndex === 0
            ? translator(SagaCommonMessages.yourPossessivePronounUpper)
            : translator(SagaCommonMessages.theirPossessivePronounUpper);
    }, [driverIndex, translator]);

    const availableClaimTypes = useMemo(() => {
        const metadataTypes = viewModelService.productMetadata.get('pc').types;
        const portalsCategory = metadataTypes.getTypelist('GwApplication_Ext').getCode('Portals');
        const allClaimTypes = metadataTypes.getTypelist('MotorClaimType_Ext').codes;
        const portalsClaimTypes = _.filter(allClaimTypes, (claimType) => { return claimType.belongsToCategory(portalsCategory); });
        return _.map(portalsClaimTypes, (portalClaimType) => {
            return {
                code: portalClaimType.code,
                name: translator({
                    id: portalClaimType.name,
                    defaultMessage: portalClaimType.name
                })
            };
        });
    }, []);

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidateParent]);

    useEffect(() => {
        return () => {
            setTimeout(() => {
                if (!window.location.hash.includes(wizardContext.currentStep.path) && !hasSavedClaim.current
                    && !selectedClaim.aspects.subtreeValid && onCancelClaim) { // only change on change of location
                    onCancelClaim();
                    const driverPath = path.split(']')[0];
                    if (_.get(modelVM, driverPath).claims.length === 0) {
                        _.get(modelVM, driverPath).hasMotorClaims.value = undefined;
                        updateData(modelVM);
                    }
                }
            });
        };
    }, [selectedClaim]);

    useEffect(() => {
        return () => {
            if (onValidateParent) {
                // workaround when component was not valid but is no longer needed (hidden)
                onValidateParent(true, id);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSaveClaim = useCallback(() => {
        if (!isComponentValid) {
            events.emit(CommonConstants.SHOW_VALIDATION_ERROR_EVENT);
            return;
        }
        hasSavedClaim.current = true;
        onSaveClaimParent();
    }, [isComponentValid, onSaveClaimParent]);

    const calculateClaimDayToSet = useCallback((dateDto) => {
        return SpmUtils.calculateClaimAndConvictionDay(dateDto, modelVM, 'baseData.periodStartDate.value');
    }, [modelVM]);

    useSgHandleSubQuestions({
        updateModel: updateData,
        updateInvalidSubQuestions,
        modelVM,
        paths: [
            `${path}.claimDate`,
            `${path}.type`,
            `${path}.whoWasAtFault`
        ]
    });

    const handleAggClaimsDatalayer = useCallback(() => {
        const allClaimsAreValid = _.every(modelVM.lobData.privateMotor.coverables.spmDrivers.children, (driver) => {
            return driver.claims.aspects.subtreeValid;
        });
        if (allClaimsAreValid) {
            SpmUtils.trackClaimsChange(modelVM);
        }
    }, [modelVM]);

    const handleClaimTypeUpdate = useCallback((value) => {
        const newModelVM = _.clone(modelVM);
        _.set(newModelVM, `${path}.type`, value);
        if (_.get(newModelVM, `${path}.whoWasAtFault.aspects.availableValues.length`) === 1) {
            _.set(newModelVM, `${path}.whoWasAtFault`, _.get(newModelVM, `${path}.whoWasAtFault.aspects.availableValues[0].code`));
        } else {
            _.set(newModelVM, `${path}.whoWasAtFault`, undefined);
        }
        updateData(newModelVM);
        if (isAggregator) {
            handleAggClaimsDatalayer();
        }
    }, [handleAggClaimsDatalayer, isAggregator, modelVM, path, updateData]);

    const handleClaimFaultUpdate = useCallback((value) => {
        const newModelVM = _.clone(modelVM);
        _.set(newModelVM, `${path}.whoWasAtFault`, value);
        updateData(newModelVM);
        if (isAggregator) {
            handleAggClaimsDatalayer();
        }
    }, [handleAggClaimsDatalayer, isAggregator, modelVM, path, updateData]);

    const faultHelpText = useMemo(() => {
        return (
            <>
                <br />
                <b>{translator(messages.FaultHelpQuestion)}</b>
                <br />
                {translator(messages.FaultHelpAnswer)}
                <br />
                <br />
                <b>{translator(messages.NotFaultHelpQuestion)}</b>
                <br />
                {translator(messages.NotFaultHelpAnswer)}
            </>
        );
    }, [translator]);

    const claimFaultStatusLabelAggs = useMemo(() => {
        const toggleExpanded = (event) => {
            event.preventDefault();
            setFaultStatusSubLabelExpanded(!faultStatusSubLabelExpanded);
        };
        return (
            <div className="sgClaimFaultStatusLabelAggs">
                {translator(messages.ClaimFaultLabelAggsText)}
                <div>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <Link
                        href="#"
                        id="lol"
                        onClick={toggleExpanded}
                    >
                        <span className="sgClaimFaultStatusAggsExpander"><i className="mir mi-info" /></span>
                    </Link>
                </div>
            </div>
        );
    }, [faultStatusSubLabelExpanded, translator]);

    const overrides = {
        claimDate: {
            path: `${path}.claimDate`,
            onValidate: onValidate,
            updateData,
            modelVM,
            minDate: moment().add(-5, 'years').toDate(),
            maxDate: new Date(),
            calculateDayToSet: calculateClaimDayToSet,
            datePickerTypeDesktop: SgDateInputTypes.MONTH_YEAR_PICKER,
            datePickerTypeMobile: SgDateInputTypes.MONTH_YEAR_INPUTS,
            showErrors: invalidSubQuestions.includes(`${path}.claimDate`),
            label: isAggregator ? translator(messages.ClaimDateLabelAggs, { possessivePronoun }) : translator(messages.ClaimDateLabel)
        },
        claimType: {
            id: `claimType${adobeSuffix}`,
            path: `${path}.type`,
            onValueChange: handleClaimTypeUpdate,
            onValidate: onValidate,
            showErrors: invalidSubQuestions.includes(`${path}.type`),
            label: isAggregator ? translator(messages.ClaimTypeLabelAggs, { possessivePronoun }) : translator(messages.ClaimTypeLabel),
            availableValues: availableClaimTypes
        },
        claimFault: {
            id: `claimFault${adobeSuffix}`,
            path: `${path}.whoWasAtFault`,
            onValidate: onValidate,
            showErrors: invalidSubQuestions.includes(`${path}.whoWasAtFault`),
            visible: !isAggregator && !_.isNil(_.get(modelVM, `${path}.type.value`))
                && _.get(modelVM, `${path}.whoWasAtFault.aspects.availableValues.length`) > 1,
            availableValues: [
                { code: SpmConstants.CLAIM_FAULT_CUSTOMER, name: translator(SagaCommonMessages.yes) },
                { code: SpmConstants.CLAIM_FAULT_3RD_PARTY, name: translator(SagaCommonMessages.no) }
            ],
            secondaryLabel: faultHelpText,
            onValueChange: handleClaimFaultUpdate
        },
        claimFaultAggs: {
            id: `claimFault${adobeSuffix}`,
            path: `${path}.whoWasAtFault`,
            onValidate: onValidate,
            showErrors: invalidSubQuestions.includes(`${path}.whoWasAtFault`),
            visible: isAggregator && !_.isNil(_.get(modelVM, `${path}.type.value`))
                && _.get(modelVM, `${path}.whoWasAtFault.aspects.availableValues.length`) > 1,
            label: claimFaultStatusLabelAggs,
            secondaryLabel: faultStatusSubLabelExpanded ? faultHelpText : '',
            onValueChange: handleClaimFaultUpdate
        },
        driversClaimsSave: {
            visible: !isAggregator
        },
        driversClaimsCancel: {
            visible: !isAggregator
        },
        claimNumberHeader: {
            visible: showClaimNumberHeader === true,
            content: showClaimNumberHeader ? translator(claimHeaderMessage, { claimNumber: (claimIndex + 1).toString() }) : null,
        },
    };

    const resolvers = {
        resolveCallbackMap: {
            onSaveClaim: onSaveClaim,
            onCancelClaim: onCancelClaim
        },
        resolveComponentMap: {
            SgDate: SgDate,
            SgSelect: SgSelect
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={modelVM}
            showErrors={showErrors}
            onModelChange={updateData}
            onValidationChange={onValidate}
            overrideProps={overrides}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
        />
    );
}

SgSpmClaimForm.defaultProps = {
    isAggregator: false
};

export default SgSpmClaimForm;
